import { OPERATIONS, type OperationId } from "./contracts/operations";
import { PENDING_OPERATIONS, type PendingOperation } from "./contracts/pending";

/**
 * The only HTTP client in the admin console. It owns the base URL, credentials (Better Auth
 * session cookie), request headers and ids, JSON parsing, error normalization and
 * unauthenticated handling.
 *
 * It can only call operations defined in the backend contract (./contracts/operations.ts).
 * There is deliberately no way to pass an arbitrary URL.
 */

export type QueryValue = string | number | boolean | null | undefined;

export interface TransportRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}
export interface TransportResponse {
  status: number;
  body: unknown;
}
export type Transport = (req: TransportRequest) => Promise<TransportResponse>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: unknown;
  constructor(o: { status: number; code: string; message: string; requestId?: string | null; details?: unknown }) {
    super(o.message);
    this.name = "ApiError";
    this.status = o.status;
    this.code = o.code;
    this.requestId = o.requestId ?? null;
    this.details = o.details;
  }
  get isUnauthenticated() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isNetwork() {
    return this.status === 0;
  }
  get userMessage(): string {
    if (this.isNetwork) return "Can't reach the Rogue API. Check the network or VPN.";
    if (this.isUnauthenticated) return "Your session has ended. Sign in again.";
    if (this.isForbidden) return "Your admin role doesn't allow this.";
    if (this.isNotFound) return "Not found.";
    if (this.status >= 500) return `The API failed${this.requestId ? ` (request ${this.requestId.slice(-8)})` : ""}.`;
    return this.message || "The request could not be completed.";
  }
}

/**
 * TODO: BACKEND API CONTRACT REQUIRED
 * Thrown by adapters for operations the backend contract doesn't define yet. The UI renders it
 * as "awaiting backend API contract" instead of pretending the feature works.
 */
export class ContractNotImplementedError extends Error {
  readonly operation: PendingOperation;
  constructor(operation: PendingOperation) {
    super(`Backend API contract not implemented: ${operation}`);
    this.name = "ContractNotImplementedError";
    this.operation = operation;
  }
  get description() {
    return PENDING_OPERATIONS[this.operation];
  }
}

/** TODO: BACKEND API CONTRACT REQUIRED — use in adapters instead of inventing an endpoint. */
export function contractRequired(operation: PendingOperation): never {
  throw new ContractNotImplementedError(operation);
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
export const isContractError = (e: unknown): e is ContractNotImplementedError => e instanceof ContractNotImplementedError;

export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.userMessage;
  if (isContractError(e)) return "This action isn't in the backend API contract yet.";
  return e instanceof Error ? e.message : "Something went wrong.";
}

export function newRequestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

export const fetchTransport: Transport = async (req) => {
  let res: Response;
  try {
    res = await fetch(req.url, { method: req.method, headers: req.headers, body: req.body, credentials: "include", signal: req.signal });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    return { status: 0, body: null };
  }
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { error: { code: "INVALID_RESPONSE", message: "The API returned a non-JSON response" } };
    }
  }
  return { status: res.status, body };
};

function normalizeError(status: number, body: unknown, requestId: string): ApiError {
  // Rogue: { error: { code, message } } — Better Auth: { code, message }
  const b = body as { error?: { code?: string; message?: string; details?: unknown; requestId?: string }; code?: string; message?: string } | null;
  const e = b?.error ?? (b && typeof b.message === "string" ? { code: b.code, message: b.message } : undefined);
  return new ApiError({
    status,
    code: e?.code ?? (status === 0 ? "NETWORK" : `HTTP_${status}`),
    message: e?.message ?? (status === 0 ? "Network error" : `Request failed (${status})`),
    requestId: (b?.error?.requestId as string | undefined) ?? requestId,
    details: b?.error?.details,
  });
}

const unauthorizedListeners = new Set<() => void>();
export function onUnauthorized(fn: () => void) {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

export interface CallOptions {
  /** Values for `{placeholders}` in the operation's path template (URL-encoded). */
  params?: Record<string, string>;
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Non-2xx statuses whose body is still a valid response (e.g. /ready answers 503). */
  acceptStatuses?: number[];
}

export interface ApiClient {
  call<T>(operation: OperationId, options?: CallOptions): Promise<T>;
}

/** Fills a contract path template; every placeholder must be provided. */
export function operationPath(operation: OperationId, params: Record<string, string> = {}): string {
  return OPERATIONS[operation].path.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params[key];
    if (value === undefined || value === "") throw new Error(`Missing path parameter "${key}" for ${operation}`);
    return encodeURIComponent(value);
  });
}

/** Admin list convention: `?q=&page=&pageSize=&sort=&order=&<filter>=`. */
export function listQueryParams(q: { q?: string; page: number; pageSize: number; sort?: string; order?: string; filters: object }): Record<string, QueryValue> {
  return { q: q.q, page: q.page, pageSize: q.pageSize, sort: q.sort, order: q.order, ...(q.filters as Record<string, QueryValue>) };
}

export function createApiClient(baseUrl: string, transport: Transport = fetchTransport): ApiClient {
  const base = baseUrl.replace(/\/+$/, "");
  return {
    async call<T>(operation: OperationId, options: CallOptions = {}) {
      const op = OPERATIONS[operation];
      const requestId = newRequestId();
      const search = new URLSearchParams();
      for (const [k, v] of Object.entries(options.query ?? {})) if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
      const url = `${base}${operationPath(operation, options.params)}${search.size ? `?${search}` : ""}`;
      const headers: Record<string, string> = { Accept: "application/json", "X-Request-Id": requestId };
      if (options.body !== undefined) headers["Content-Type"] = "application/json";
      const res = await transport({
        method: op.method,
        url,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
      });
      if ((res.status >= 200 && res.status < 300) || options.acceptStatuses?.includes(res.status)) return res.body as T;
      const error = normalizeError(res.status, res.body, requestId);
      if (error.isUnauthenticated && operation !== "signInEmail") for (const l of unauthorizedListeners) l();
      throw error;
    },
  };
}
